import { ENABLE_MAINTENANCE_MODE } from './index';
import { lazyImport } from '../utils/lazyImport/lazyImport';

/**
 *
 * interface IRoute {
 *   path: string;
 *   Component?: React.LazyExoticComponent<React.ReactNode>;
 *   private?: boolean;
 *   queryArguments?: string[];
 *   exact?: boolean; // Exact Match für die Route
 *   sorting?: number; // Sortierung der Route
 *   redirects?: string[]; // Redirects welche auf die Route Umleiten
 *   tinted?: boolean; // Menu wird gefärbt im initialen Zustand
 *   theme?: 'calm' | 'podcast' | 'work'; // Theme für die Seite
 *   hideFooterLinks?: boolean; // Footer Links werden nicht angezeigt
 *   cooperation?: boolean; // Kooperation mit Krankenkasse
 *   disableAuthentication?: boolean; // Authentifizierung wird deaktiviert
 *   cooperationColor?: 'green' | 'blue'; // Farbe für die Kooperation
 *   hideMenuLinks: boolean; // Footer wird angezeigt
 *   hideFooter: boolean; // Footer wird angezeigt
 *   announcer?: string; // Accessibility Announcer Text
 *   redirectTo?: string; // Umleitung zu einer anderen Seite, wenn die Route aufgerufen wird
 * }
 *
 * @typedef {Object.<string, IRoute>} Routes
 */

export let Routes = {
  home: {
    exact: true,
    path: '/',
    Component: lazyImport(() => import('components/Home/Home')),
    announcer: 'Balloon - Home'
  },
  appLegacyImprint: {
    path: '/app-resources/managed-2/HTML/imprint/imprint.html',
    Component: lazyImport(() => import('Page/AppImprintPage/AppImprintPage'))
  },
  appLegacyPrivacy: {
    path: '/app-resources/managed-2/HTML/terms/privacy.html',
    Component: lazyImport(() => import('Page/AppPrivacyPage/AppPrivacyPage'))
  },
  appLegacyTerms: {
    path: '/app-resources/managed-2/HTML/terms/terms/terms.html',
    Component: lazyImport(() => import('Page/AppTermsPage/AppTermsPage'))
  },
  imprint: {
    path: '/imprint',
    Component: lazyImport(() => import('components/Imprint')),
    announcer: 'Impressum'
  },
  checkout: {
    path: '/checkout',
    tinted: true,
    Component: lazyImport(() => import('Page/Checkout/Checkout')),
    announcer: 'Kauf abschließen'
  },
  voucher_manual: {
    path: '/gutschein/anleitung',
    tinted: true,
    Component: lazyImport(() => import('components/Landingpages/VoucherManual/VoucherManual')),
    announcer: 'Gutschein - Anleitung'
  },
  voucher: {
    path: '/gutschein',
    private: true,
    tinted: true,
    Component: lazyImport(() => import('components/Profile/Profile')),
    announcer: 'Gutschein einlösen'
  },
  checkoutConfirm: {
    path: '/checkout-confirm',
    Component: lazyImport(() => import('Page/CheckoutConfirm/CheckoutConfirm')),
    announcer: 'Kauf bestätigen'
  },
  podcast: {
    path: '/podcast',
    tinted: true,
    theme: 'podcast',
    Component: lazyImport(() => import('components/Landingpages/Podcast/PodcastLandingPage')),
    announcer: 'Podcast'
  },
  category: {
    path: '/bibliothek/:slug',
    queryArguments: ['slug'],
    tinted: true,
    redirects: ['/category/:slug'],
    Component: lazyImport(() => import('Page/MeditationPage/MeditationPage')),
    announcer: 'Kategorie {{slug}}'
  },
  library: {
    path: '/bibliothek',
    tinted: true,
    Component: lazyImport(() => import('Page/MeditationPage/MainMeditationPage')),
    announcer: 'Meditationen'
  },
  singlePlayer: {
    path: '/play/single/:slug',
    tinted: true,
    queryArguments: ['slug'],
    Component: lazyImport(() => import('Page/SinglePlayerPage/SinglePlayerPage')),
    announcer: '{{slug}} abspielen'
  },
  webplayer: {
    path: '/play/course/:slug',
    tinted: true,
    queryArguments: ['slug'],
    Component: lazyImport(() => import('Page/CoursePlayerPage/CoursePlayerPage')),
    announcer: '{{slug}} Kurs abspielen'
  },
  thankyou: {
    path: '/thankyou',
    tinted: true,
    Component: lazyImport(() => import('Page/ThankYou/ThankYou')),
    private: true,
    announcer: 'Danke für deinen Kauf'
  },
  thankyougift: {
    path: '/thankyougift',
    tinted: true,
    Component: lazyImport(() => import('Page/ThankYou/ThankYouGift')),
    announcer: 'Danke für deinen Kauf'
  },
  thankyouloggedout: {
    path: '/thankyouloggedout',
    tinted: true,
    Component: lazyImport(() => import('Page/ThankYou/ThankYouLoggedOut')),
    announcer: 'Danke für deinen Kauf'
  },
  thankyoupurchaseassociated: {
    path: '/thankyoupurchaseassociated',
    tinted: true,
    Component: lazyImport(() => import('Page/ThankYou/ThankYouPurchaseAssociated')),
    announcer: 'Danke für deinen Kauf'
  },
  thankyouupgrade: {
    path: '/thankyouupgrade',
    tinted: true,
    Component: lazyImport(() => import('Page/ThankYou/ThankYouUpgrade')),
    private: true,
    announcer: 'Danke für dein Upgrade'
  },
  externalLegacyFacebookPrivacy: {
    path: '/facebook-privacy',
    hideFooterLinks: true,
    tinted: true,
    Component: lazyImport(() => import('Page/ExternalFacebookPrivacyPage/ExternalFacebookPrivacyPage'))
  },
  externalLegacyPrivacyPolicy: {
    path: '/privacy-policy',
    hideFooterLinks: true,
    tinted: true,
    Component: lazyImport(() => import('Page/ExternalPrivacyPolicyPage/ExternalPrivacyPolicyPage'))
  },
  externalLegacyTerms: {
    path: '/terms',
    hideFooterLinks: true,
    tinted: true,
    Component: lazyImport(() => import('Page/ExternalTermsPage/ExternalTermsPage')),
    announcer: 'Nutzungsbedingungen'
  },
  externalLegacyHealth: {
    path: '/health',
    hideFooterLinks: true,
    tinted: true,
    Component: lazyImport(() => import('Page/ExternalHealthPage/ExternalHealthPage')),
    announcer: 'Nutzungsbedingungen'
  },
  externalFacebookPrivacy: {
    path: '/legal/facebook-privacy',
    tinted: true,
    hideFooterLinks: true,
    Component: lazyImport(() => import('Page/ExternalFacebookPrivacyPage/ExternalFacebookPrivacyPage')),
    announcer: 'Facebook Datenschutzrichtlinie'
  },
  externalPrivacyPolicy: {
    path: '/legal/privacy-policy',
    tinted: true,
    hideFooterLinks: true,
    Component: lazyImport(() => import('Page/ExternalPrivacyPolicyPage/ExternalPrivacyPolicyPage')),
    announcer: 'Datenschutzrichtlinie'
  },
  externalTerms: {
    path: '/legal/terms',
    tinted: true,
    hideFooterLinks: true,
    Component: lazyImport(() => import('Page/ExternalTermsPage/ExternalTermsPage')),
    announcer: 'Nutzungsbedingungen'
  },
  externalHealth: {
    path: '/legal/health',
    tinted: true,
    hideFooterLinks: true,
    Component: lazyImport(() => import('Page/ExternalHealthPage/ExternalHealthPage')),
    announcer: 'Gesundheitsinformationen'
  },
  webPrivacy: {
    path: '/legal/web/privacy',
    tinted: true,
    hideFooterLinks: true,
    Component: lazyImport(() => import('Page/PrivacyPage/PrivacyPage')),
    announcer: 'Datenschutzbestimmungen'
  },
  webTerms: {
    path: '/legal/web/terms',
    tinted: true,
    hideFooterLinks: true,
    Component: lazyImport(() => import('Page/TermsPage/TermsPage')),
    announcer: 'Nutzungsbedingungen'
  },
  webHealth: {
    path: '/legal/web/health',
    tinted: true,
    hideFooterLinks: true,
    Component: lazyImport(() => import('Page/HealthPage/HealthPage')),
    announcer: 'Gesundheitsinformationen'
  },
  webHealthBenefit: {
    path: '/legal/web/health-benefit',
    tinted: true,
    hideFooterLinks: true,
    Component: lazyImport(() => import('Page/HealthBenefitPage/HealthBenefitPage')),
    announcer: 'Vorteile für die Gesundheit'
  },
  webAccessibility: {
    path: '/legal/web/accessibility',
    tinted: true,
    hideFooterLinks: true,
    Component: lazyImport(() => import('Page/AccessibilityPage/AccessibilityPage')),
    announcer: 'Barrierefreiheitserklärung'
  },
  login: {
    path: '/login',
    tinted: true,
    Component: lazyImport(() => import('Page/AuthenticationPage/AuthenticationPage')),
    announcer: 'Anmelden'
  },
  signup: {
    path: '/signup',
    tinted: true,
    Component: lazyImport(() => import('Page/AuthenticationPage/AuthenticationPage')),
    announcer: 'Registrieren'
  },
  confirmMail: {
    path: '/web/login',
    queryArguments: ['code'],
    tinted: true,
    Component: lazyImport(() => import('components/SignInUserWithOnetimePasswordBox')),
    announcer: 'E-Mail bestätigen'
  },
  confirmMailCode: {
    path: '/web/login/:code?',
    queryArguments: ['code'],
    tinted: true,
    Component: lazyImport(() => import('components/SignInUserWithOnetimePasswordBox')),
    announcer: 'E-Mail bestätigen'
  },
  fallbackAppOtp: {
    path: '/app/login/:code?',
    queryArguments: ['code'],
    Component: lazyImport(() => import('components/FallbackAppOtpPage')),
    announcer: 'Anmelden'
  },
  paymenterror: {
    path: '/paymenterror',
    tinted: true,
    Component: lazyImport(() => import('Page/Checkout/PaymentError')),
    announcer: 'Zahlungsfehler'
  },
  paymenterror_nostorage: {
    path: '/payment-referererror',
    Component: lazyImport(() => import('Page/Checkout/PaymentErrorNoStorage')),
    announcer: 'Zahlungsfehler'
  },
  upgradeerror: {
    path: '/upgradeerror',
    Component: lazyImport(() => import('components/SubscriptionUpgrade/UpgradeError')),
    announcer: 'Upgrade Fehler'
  },
  email_confirmation: {
    path: '/email_confirmation',
    tinted: true,
    Component: lazyImport(() => import('components/EmailConfirmation')),
    announcer: 'E-Mail bestätigt'
  },
  email_confirmation_error: {
    path: '/email_confirmation_error',
    Component: lazyImport(() => import('components/EmailConfirmationError')),
    announcer: 'E-Mail Bestätigungsfehler'
  },
  upgrade: {
    path: '/upgrade',
    tinted: true,
    Component: lazyImport(() => import('Page/SubscriptionUpgradePage/SubscriptionUpgradePage')),
    announcer: 'Abo Upgrade'
  },
  withdrawCancelation: {
    path: '/withdraw-cancelation',
    Component: lazyImport(() => import('containers/Checkout/WithdrawCancelationContainer')),
    announcer: 'Abo Kündigung widerrufen'
  },
  terminateSuccess: {
    path: '/terminate-success',
    tinted: true,
    Component: lazyImport(() => import('Page/TerminateUserAboSuccess/TerminateUserAboSuccess')),
    announcer: 'Abo Kündigung erfolgreich'
  },
  terminateFailed: {
    path: '/terminate-failed',
    tinted: true,
    Component: lazyImport(() => import('Page/TerminateUserAboFailed/TerminateUserAboFailed')),
    announcer: 'Abo Kündigung fehlgeschlagen'
  },
  terminate: {
    path: '/terminate',
    tinted: true,
    Component: lazyImport(() => import('Page/TerminateUserAbo/TerminateUserAbo')),
    announcer: 'Abo Kündigung'
  },
  fallbackApp: {
    path: '/app/*',
    tinted: true,
    Component: lazyImport(() => import('components/FallbackAppPage'))
  },
  fallbackAppView: {
    path: '/view/*',
    Component: lazyImport(() => import('components/FallbackAppPage'))
  },
  krankenkasseSurveyWrong: {
    path: '/krankenkasse/survey-wrong',
    hideMenuLinks: true,
    Component: lazyImport(() => import('components/Landingpages/HealthInsurance/Survey/Wrong')),
    announcer: 'Krankenkassen Umfrage - Falsch'
  },
  krankenkasseSurveyRight: {
    path: '/krankenkasse/survey-right',
    hideMenuLinks: true,
    Component: lazyImport(() => import('components/Landingpages/HealthInsurance/Survey/Right')),
    announcer: 'Krankenkassen Umfrage - Richtig'
  },
  krankenkasseSurveyMaybe: {
    path: '/krankenkasse/survey-maybe',
    hideMenuLinks: true,
    Component: lazyImport(() => import('components/Landingpages/HealthInsurance/Survey/Maybe')),
    announcer: 'Krankenkassen Umfrage - Vielleicht'
  },
  trustPortal: {
    path: '/security',
    Component: lazyImport(() => import('Page/TrustPortalPage/TrustPortalPage')),
    announcer: 'Sicherheitsportal'
  },
  externalPrivacy: {
    path: '/legal/privacy',
    tinted: true,
    hideFooterLinks: true,
    Component: lazyImport(() => import('Page/ExternalFacebookPrivacyPage/ExternalFacebookPrivacyPage')),
    announcer: 'Sicherheitsportal'
  },
  webappLegacyAccessibility: {
    path: '/webapp-resources/health_accessibility.html',
    hideFooterLinks: true,
    tinted: true,
    Component: lazyImport(() => import('Page/AccessibilityPage/AccessibilityPage'))
  },
  webappLegacyHealth: {
    path: '/webapp-resources/health.html',
    hideFooterLinks: true,
    tinted: true,
    Component: lazyImport(() => import('Page/HealthPage/HealthPage'))
  },
  webappLegacyHealthBenefit: {
    path: '/webapp-resources/health_benefit.html',
    hideFooterLinks: true,
    tinted: true,
    Component: lazyImport(() => import('Page/HealthBenefitPage/HealthBenefitPage'))
  },
  webappLegacyPrivacy: {
    path: '/webapp-resources/privacy.html',
    hideFooterLinks: true,
    tinted: true,
    Component: lazyImport(() => import('Page/PrivacyPage/PrivacyPage'))
  },
  webappLegacyTerms: {
    path: '/webapp-resources/terms.html',
    hideFooterLinks: true,
    tinted: true,
    Component: lazyImport(() => import('Page/TermsPage/TermsPage'))
  },
  insuranceCooperationAuthentication: {
    hideFooterLinks: true,
    path: '/insurance-cooperation-authentication',
    Component: lazyImport(() => import('Page/InsuranceCooperationAuthenticationPage/InsuranceCooperationAuthenticationPage'))
  },
  gothaer: {
    cooperation: true,
    hideMenuLinks: true,
    path: '/krankenkasse/gothaer',
    exact: true,
    theme: 'calm',
    redirects: ['/gothaer'],
    Component: lazyImport(() => import('components/Landingpages/Gothaer/GothaerLandingPage')),
    announcer: 'Krankenkasse - Gothaer'
  },
  dakBgmHealthInsuranceCooperationCourse: {
    hideMenuLinks: true,
    disableAuthentication: true,
    theme: 'calm',
    exact: true,
    redirects: ['/dak-bgm'],
    path: '/krankenkasse/dak-bgm',
    Component: lazyImport(() => import('Page/HealthInsuranceDakBgmCooperationOfferPage/HealthInsuranceDakBgmCooperationOfferPage')),
    announcer: 'Krankenkasse - DAK BGM'
  },
  hkkBgmHealthInsuranceCooperationCourse: {
    hideMenuLinks: true,
    disableAuthentication: true,
    theme: 'calm',
    exact: true,
    path: '/krankenkasse/hkk-bgm',
    redirectTo: '/krankenkasse',
    // keep Component prop for later use, when the offer page for hkk will be available again
    Component: lazyImport(() => import('Page/HealthInsuranceHkkBgmCooperationOfferPage/HealthInsuranceHkkBgmCooperationOfferPage')),
    announcer: 'Krankenkasse - hkk BGM'
  },
  healthInsuranceCooperation: {
    hideMenuLinks: true,
    disableAuthentication: true,
    theme: 'calm',
    cooperation: true,
    exact: true,
    path: '/krankenkasse/:cooperation',
    queryArguments: ['cooperation'],
    Component: lazyImport(() => import('Page/HealthInsuranceCooperationPartnerPage/HealthInsuranceCooperationPartnerPage')),
    announcer: 'Krankenkasse - {{cooperation}}'
  },

  tkHealthInsuranceCooperationCourse: {
    hideMenuLinks: true,
    disableAuthentication: true,
    theme: 'calm',
    exact: true,
    path: '/krankenkasse/techniker/:course',
    queryArguments: ['course'],
    Component: lazyImport(() => import('Page/HealthInsuranceTkCooperationOfferPage/HealthInsuranceTkCooperationOfferPage')),
    announcer: 'Krankenkasse - Techniker Krankenkasse {{course}}'
  },
  healthInsuranceCooperationCourse: {
    hideMenuLinks: true,
    disableAuthentication: true,
    cooperation: true,
    cooperationColor: 'blue',
    theme: 'calm',
    exact: true,
    path: '/krankenkasse/:cooperation/:course',
    queryArguments: ['cooperation', 'course'],
    Component: lazyImport(() => import('Page/HealthInsuranceCooperationOfferPage/HealthInsuranceCooperationOfferPage')),
    announcer: 'Krankenkasse - {{cooperation}} {{course}}'
  },

  profile: {
    path: '/profile/*',
    tinted: true,
    private: true,
    Component: lazyImport(() => import('components/Profile/Profile')),
    announcer: 'Profil'
  },
  profilePersonalData: {
    path: '/profile/personal-data',
    tinted: true,
    private: true,
    Component: lazyImport(() => import('components/Profile/Profile')),
    announcer: 'Profil - Persönliche Daten'
  },
  profileSubscriptionStatus: {
    path: '/profile/subscription-status',
    tinted: true,
    private: true,
    Component: lazyImport(() => import('components/Profile/Profile')),
    announcer: 'Profil - Abo Status'
  },
  deeplinkConfirmMail: {
    path: '/web/confirm_email_change/:code?',
    queryArguments: ['code'],
    Component: lazyImport(() => import('components/Profile/Profile')),
    announcer: 'E-Mail Änderung bestätigen'
  },
  maintenance: {
    path: '/wartung',
    Component: lazyImport(() => import('components/Application/Maintenance/Maintenance')),
    hideFooter: true,
    tinted: true,
    hideMenuLinks: true,
    announcer: 'Wartung'
  }
};

if (ENABLE_MAINTENANCE_MODE) {
  Routes = {
    maintenance: {
      path: '/wartung',
      Component: lazyImport(() => import('components/Application/Maintenance/Maintenance')),
      hideFooter: true,
      tinted: true,
      hideMenuLinks: true,
      announcer: 'Wartung'
    }
  };
}
